import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SEO, { PAGE_SEO } from "./SEO";
import {
  Settings,
  TrendingUp,
  Lightbulb,
  Package,
  ArrowRight,
  Target,
  Wind,
  Zap,
  CheckCircle2,
  Circle,
  AlertCircle,
} from "lucide-react";
import { UserProfile } from "../types";
import { recordProductEvent } from "../services/productEvents";
import EquipmentFitting from "./EquipmentFitting";
import Distances from "./Distances";
import Recommendations from "./Recommendations";
import ShotAdvisor from "./ShotAdvisor";
import SwingAnalyzer from "./SwingAnalyzer";
import BallMatching from "./BallMatching";

interface ClubOptimizerHubProps {
  userProfile: UserProfile | null;
  onProfileUpdate: (profile: UserProfile) => Promise<boolean> | void;
}

type TabType =
  | "health-check"
  | "gapping"
  | "shot-advisor"
  | "swing-tips"
  | "ball-match"
  | "equipment-fitting";

const TAB_CONFIG: Array<{
  id: TabType;
  label: string;
  sublabel: string;
  icon: React.ElementType;
  gradient: string;
  primary?: boolean;
}> = [
  {
    id: "health-check",
    label: "Health Check",
    sublabel: "Recommendations",
    icon: Lightbulb,
    gradient: "from-indigo-500 to-blue-600",
    primary: true,
  },
  {
    id: "gapping",
    label: "Gapping",
    sublabel: "Analysis",
    icon: TrendingUp,
    gradient: "from-orange-500 to-amber-600",
  },
  {
    id: "shot-advisor",
    label: "Shot",
    sublabel: "Advisor",
    icon: Target,
    gradient: "from-emerald-500 to-teal-600",
  },
  {
    id: "swing-tips",
    label: "Swing",
    sublabel: "Tips",
    icon: Wind,
    gradient: "from-rose-500 to-pink-600",
  },
  {
    id: "ball-match",
    label: "Ball",
    sublabel: "Match",
    icon: Zap,
    gradient: "from-violet-500 to-purple-600",
  },
  {
    id: "equipment-fitting",
    label: "Equipment",
    sublabel: "Fitting",
    icon: Settings,
    gradient: "from-purple-500 to-violet-600",
  },
];

const ClubOptimizerHub: React.FC<ClubOptimizerHubProps> = ({
  userProfile,
  onProfileUpdate,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const hasCompletedFitting =
    userProfile &&
    userProfile.golfBag &&
    userProfile.golfBag.length > 0 &&
    userProfile.age !== undefined &&
    userProfile.height !== undefined;

  const initialTab = (searchParams.get("tab") as TabType) || undefined;
  const [activeTab, setActiveTab] = useState<TabType>(
    initialTab || "health-check"
  );
  const [hasInitialized, setHasInitialized] = useState(!!initialTab);

  useEffect(() => {
    void recordProductEvent('CLUB_OPTIMIZER_USED');
  }, []);

  // Auto-navigate to health-check if user has completed fitting
  useEffect(() => {
    if (!hasInitialized && hasCompletedFitting) {
      setActiveTab("health-check");
      setHasInitialized(true);
    }
  }, [hasCompletedFitting, hasInitialized]);

  // Respond to URL tab param changes (from redirects)
  useEffect(() => {
    const tabParam = searchParams.get("tab") as TabType;
    if (tabParam && TAB_CONFIG.some((t) => t.id === tabParam)) {
      setActiveTab(tabParam);
      // Clear the param so it doesn't persist on tab switches
      searchParams.delete("tab");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
  };

  // Setup gating: check if bag and distances are ready
  const hasClubs = userProfile?.golfBag && userProfile.golfBag.length > 0;
  const clubsWithDistances = userProfile?.golfBag?.filter(c => c.distance && c.distance > 0) || [];
  const hasEnoughDistances = clubsWithDistances.length >= 3;
  const isSetupComplete = hasClubs && hasEnoughDistances;

  const setupSteps = [
    {
      label: "Complete your golf profile quiz",
      done: userProfile?.hasCompletedOnboarding === true,
    },
    {
      label: "Add clubs to your bag",
      done: !!hasClubs,
      detail: hasClubs ? `${userProfile?.golfBag?.length ?? 0} clubs added` : "No clubs yet",
    },
    {
      label: "Confirm your club distances",
      done: hasEnoughDistances,
      detail: hasClubs ? `${clubsWithDistances.length} of ${userProfile?.golfBag?.length ?? 0} clubs have distances` : "Add clubs first",
    },
  ];

  // If setup is not complete, show setup gate
  if (!isSetupComplete) {
    return (
      <>
        <SEO {...PAGE_SEO.clubOptimizer} url="/club-optimizer" />
        <div className="max-w-2xl mx-auto p-4 sm:p-6 mt-4">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-6 sm:p-8 text-white text-center">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Settings className="w-8 h-8" />
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold mb-2">Club Optimizer</h1>
              <p className="text-purple-100 text-sm sm:text-base">
                Complete your bag setup to unlock personalized recommendations
              </p>
            </div>

            <div className="p-6 sm:p-8 space-y-4">
              {setupSteps.map((step, i) => (
                <div
                  key={i}
                  className={`flex items-start gap-4 p-4 rounded-xl border-2 transition-all ${
                    step.done
                      ? "border-green-200 bg-green-50"
                      : "border-gray-200 bg-gray-50"
                  }`}
                >
                  {step.done ? (
                    <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                  ) : (
                    <Circle className="w-6 h-6 text-gray-300 flex-shrink-0 mt-0.5" />
                  )}
                  <div>
                    <p className={`font-semibold ${step.done ? "text-green-800" : "text-gray-700"}`}>
                      Step {i + 1}: {step.label}
                    </p>
                    {step.detail && (
                      <p className={`text-sm mt-0.5 ${step.done ? "text-green-600" : "text-gray-500"}`}>
                        {step.detail}
                      </p>
                    )}
                  </div>
                </div>
              ))}

              <div className="pt-4">
                <Link
                  to="/my-golf-bag"
                  className="flex items-center justify-center gap-3 w-full px-6 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
                >
                  <Package className="w-6 h-6" />
                  {hasClubs ? "Review My Bag & Distances" : "Build My Bag"}
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>

              {hasClubs && !hasEnoughDistances && (
                <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                  <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-800">
                    Your bag has clubs but most are missing distances. 
                    Go to My Bag and confirm your distances to unlock the optimizer.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <SEO {...PAGE_SEO.clubOptimizer} url="/club-optimizer" />
      <div className="max-w-7xl mx-auto p-0 sm:p-6 space-y-0 sm:space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-none sm:rounded-2xl p-4 sm:p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 sm:space-x-4">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                <Settings className="w-6 h-6 sm:w-8 sm:h-8" />
              </div>
              <div className="min-w-0">
                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold truncate">
                  Club Optimizer
                </h1>
                <p className="text-purple-100 text-xs sm:text-sm md:text-base">
                  Analyze, optimize, upgrade
                </p>
              </div>
            </div>
            <Link
              to="/my-golf-bag"
              className="flex items-center gap-2 px-4 py-2.5 sm:px-5 sm:py-3 bg-white text-purple-700 hover:bg-purple-50 rounded-xl transition-all text-sm sm:text-base font-semibold shadow-lg hover:shadow-xl"
            >
              <Package className="w-5 h-5 sm:w-6 sm:h-6" />
              <span className="hidden sm:inline">My Bag</span>
              <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
            </Link>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-none sm:rounded-2xl shadow-lg overflow-x-hidden overflow-y-visible min-w-0">
          <div className="bg-gradient-to-r from-gray-50 to-white p-2 sm:p-3">
            <nav className="grid grid-cols-3 sm:grid-cols-6 gap-1.5 sm:gap-2">
              {TAB_CONFIG.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={`relative overflow-hidden rounded-xl sm:rounded-2xl transition-all duration-300 ${
                      isActive
                        ? "shadow-lg transform scale-105"
                        : "shadow hover:shadow-md"
                    } ${tab.primary && !isActive ? "ring-2 ring-indigo-200" : ""}`}
                    style={
                      isActive
                        ? {
                            background: `linear-gradient(135deg, ${
                              tab.id === "health-check"
                                ? "#6366f1, #3b82f6"
                                : tab.id === "gapping"
                                  ? "#f97316, #ea580c"
                                  : tab.id === "shot-advisor"
                                    ? "#10b981, #14b8a6"
                                    : tab.id === "swing-tips"
                                      ? "#f43f5e, #ec4899"
                                      : tab.id === "ball-match"
                                        ? "#8b5cf6, #a855f7"
                                        : "#a855f7, #7c3aed"
                            })`,
                          }
                        : { background: "white" }
                    }
                  >
                    <div className="p-2.5 sm:p-3 min-h-[48px] flex flex-col items-center justify-center">
                      <Icon
                        className={`w-5 h-5 sm:w-7 sm:h-7 mx-auto mb-1 sm:mb-1.5 ${
                          isActive ? "text-white" : "text-gray-400"
                        }`}
                      />
                      <div
                        className={`font-bold text-[11px] sm:text-xs leading-tight ${
                          isActive ? "text-white" : "text-gray-700"
                        }`}
                      >
                        {tab.label}
                      </div>
                      <div
                        className={`text-[10px] sm:text-[11px] mt-0.5 hidden sm:block ${
                          isActive ? "text-white/90" : "text-gray-500"
                        }`}
                      >
                        {tab.sublabel}
                      </div>
                    </div>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content — min-w-0 so nested horizontal scroll (e.g. Equipment Fitting) works on iOS */}
          <div className="min-w-0">
            {activeTab === "health-check" && (
              <Recommendations userProfile={userProfile} />
            )}

            {activeTab === "gapping" && (
              <div className="p-3 sm:p-6">
                <div className="mb-4 sm:mb-6">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
                    Gapping Analysis
                  </h2>
                  <p className="text-sm sm:text-base text-gray-600">
                    Identify overlaps and gaps in your club distances
                  </p>
                </div>
                <Distances
                  userProfile={userProfile}
                  onProfileUpdate={onProfileUpdate}
                />
              </div>
            )}

            {activeTab === "shot-advisor" && (
              <div className="p-3 sm:p-6">
                <ShotAdvisor userProfile={userProfile} />
              </div>
            )}

            {activeTab === "swing-tips" && (
              <div className="p-3 sm:p-6">
                <SwingAnalyzer userProfile={userProfile} />
              </div>
            )}

            {activeTab === "ball-match" && (
              <div className="p-3 sm:p-6">
                <BallMatching userProfile={userProfile} />
              </div>
            )}

            {activeTab === "equipment-fitting" && (
              <EquipmentFitting
                userProfile={userProfile}
                onProfileUpdate={onProfileUpdate}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ClubOptimizerHub;
